import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  ClientKind,
  RegistrationUf,
  ReportFunction,
} from '../../../api/enumerations';
import { PeriodKey } from '../constants';

const VALID_PERIODS: PeriodKey[] = ['today', 'month', 'quarter', 'year'];

const isValidPeriod = (v: string | null): v is PeriodKey =>
  !!v && (VALID_PERIODS as string[]).includes(v);

export interface DashboardFilters {
  period: PeriodKey;
  uf?: RegistrationUf;
  companyId?: number;
  clientKinds: ClientKind[];
  reportFunctions: ReportFunction[];
}

export interface UseDashboardFiltersReturn {
  filters: DashboardFilters;
  setPeriod: (p: PeriodKey) => void;
  setUf: (uf: RegistrationUf | undefined) => void;
  setCompanyId: (id: number | undefined) => void;
  setClientKinds: (list: ClientKind[]) => void;
  setReportFunctions: (list: ReportFunction[]) => void;
  clearAll: () => void;
}

const parseNumberList = (raw: string | null): number[] => {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => Number(s))
    .filter((n) => !Number.isNaN(n));
};

export function useDashboardFilters(): UseDashboardFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: DashboardFilters = useMemo(() => {
    const period = isValidPeriod(searchParams.get('period'))
      ? (searchParams.get('period') as PeriodKey)
      : 'month';
    const ufRaw = Number(searchParams.get('uf'));
    const companyRaw = Number(searchParams.get('company'));
    return {
      period,
      uf:
        Number.isNaN(ufRaw) || ufRaw === 0
          ? undefined
          : (ufRaw as RegistrationUf),
      companyId:
        Number.isNaN(companyRaw) || companyRaw === 0 ? undefined : companyRaw,
      clientKinds: parseNumberList(searchParams.get('client')) as ClientKind[],
      reportFunctions: parseNumberList(
        searchParams.get('function')
      ) as ReportFunction[],
    };
  }, [searchParams]);

  const update = useCallback(
    (mutator: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutator(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setPeriod = useCallback(
    (p: PeriodKey) => update((next) => next.set('period', p)),
    [update]
  );

  const setUf = useCallback(
    (uf: RegistrationUf | undefined) =>
      update((next) => {
        if (uf === undefined) next.delete('uf');
        else next.set('uf', String(uf));
      }),
    [update]
  );

  const setCompanyId = useCallback(
    (id: number | undefined) =>
      update((next) => {
        if (id === undefined) next.delete('company');
        else next.set('company', String(id));
      }),
    [update]
  );

  const setClientKinds = useCallback(
    (list: ClientKind[]) =>
      update((next) => {
        if (list.length === 0) next.delete('client');
        else next.set('client', list.join(','));
      }),
    [update]
  );

  const setReportFunctions = useCallback(
    (list: ReportFunction[]) =>
      update((next) => {
        if (list.length === 0) next.delete('function');
        else next.set('function', list.join(','));
      }),
    [update]
  );

  const clearAll = useCallback(
    () =>
      update((next) => {
        next.delete('uf');
        next.delete('company');
        next.delete('client');
        next.delete('function');
      }),
    [update]
  );

  return {
    filters,
    setPeriod,
    setUf,
    setCompanyId,
    setClientKinds,
    setReportFunctions,
    clearAll,
  };
}
